import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const navItems = [
  { label: '首页', href: '/', isPage: true },
  {
    label: '核心业务',
    href: '/business',
    isPage: true,
    hasDropdown: true,
    children: [
      { label: '工信部碳排放申报服务', href: '/business/carbon-report', icon: 'ri-government-line', desc: '12项全流程合规申报' },
      { label: '工业AI智能化解决方案', href: '/business/industrial-ai', icon: 'ri-robot-2-line', desc: '工厂落地·智能升级' },
      { label: 'CCER/CBAM合规填报', href: '/business/ccer-cbam', icon: 'ri-global-line', desc: '国家平台直连上报' },
      { label: '增值配套服务', href: '/business/value-added', icon: 'ri-service-line', desc: '碳盘查·ESG报告' },
    ],
  },
  {
    label: '产品中心',
    href: '/products',
    isPage: true,
    hasDropdown: true,
    children: [
      { label: '碳排放申报管理系统', href: '/products/carbon-report-system', icon: 'ri-file-chart-line', desc: '工信部官方平台直连' },
      { label: '工业AI智能管控平台', href: '/products/industrial-ai-platform', icon: 'ri-cpu-line', desc: 'AI工厂全流程管控' },
      { label: 'CCER/CBAM合规数据系统', href: '/products/ccer-cbam-system', icon: 'ri-database-2-line', desc: '合规填报·数据上报' },
      { label: '碳台账区块链存证系统', href: '/products/blockchain-ledger', icon: 'ri-links-line', desc: '不可篡改·核查无忧' },
      { label: '工业能耗AI监测分析', href: '/products/energy-monitor', icon: 'ri-line-chart-line', desc: 'IoT+AI实时监测' },
    ],
  },
  {
    label: '行业解决方案',
    href: '/industry',
    isPage: true,
    hasDropdown: true,
    children: [
      { label: '钢铁冶金', href: '/solutions/steel', icon: 'ri-hammer-line' },
      { label: '水泥建材', href: '/solutions/cement', icon: 'ri-building-2-line' },
      { label: '化工行业', href: '/solutions/chemical', icon: 'ri-flask-line' },
      { label: '电力能源', href: '/solutions/power', icon: 'ri-flashlight-line' },
      { label: '装备制造', href: '/solutions/equipment', icon: 'ri-settings-3-line' },
      { label: '外贸出口制造', href: '/solutions/export', icon: 'ri-ship-line' },
      { label: '工业园区', href: '/solutions/park', icon: 'ri-community-line' },
    ],
  },
  { label: '政策资讯', href: '/news', isPage: true },
  { label: '服务支持', href: '/support', isPage: true },
  { label: '关于我们', href: '/about', isPage: true },
  { label: '联系我们', href: '/contact', isPage: true },
];

type NavItem = typeof navItems[0];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const dropdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const handleMouseEnter = (label: string) => {
    if (dropdownTimerRef.current) clearTimeout(dropdownTimerRef.current);
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    dropdownTimerRef.current = setTimeout(() => setActiveDropdown(null), 120);
  };

  const handleNavClick = (e: React.MouseEvent, item: NavItem) => {
    e.preventDefault();
    setMobileOpen(false);
    setActiveDropdown(null);
    navigate(item.href);
  };

  const isActive = (item: NavItem) => {
    if (item.href === '/') return location.pathname === '/';
    return location.pathname.startsWith(item.href);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-firmai-dark/95 backdrop-blur-md shadow-lg shadow-black/20' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            className="flex items-center gap-2.5 shrink-0 cursor-pointer"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/b5b3c1b8-bbd4-4161-b813-5c9d75b1cee8.png"
              alt="Firmai Logo"
              className="h-8 w-auto"
            />
            <span className="text-white font-display font-bold text-xl tracking-tight hidden sm:block">
              Firmai
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden xl:flex items-center gap-0.5">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.hasDropdown ? handleMouseEnter(item.label) : undefined}
                onMouseLeave={() => item.hasDropdown ? handleMouseLeave() : undefined}
              >
                <a
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item)}
                  className={`flex items-center gap-0.5 px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-md whitespace-nowrap cursor-pointer ${
                    isActive(item) ? 'text-firmai-green' : 'text-firmai-text hover:text-white hover:bg-white/5'
                  }`}
                >
                  {item.label}
                  {item.hasDropdown && (
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${activeDropdown === item.label ? 'rotate-180' : ''}`} />
                    </span>
                  )}
                </a>

                {/* Dropdown */}
                {item.hasDropdown && item.children && activeDropdown === item.label && (
                  <div
                    className="absolute top-full left-0 mt-1 bg-[#0D1117] border border-firmai-border rounded-xl shadow-2xl shadow-black/30 overflow-hidden z-50 animate-dropdown"
                    style={{ minWidth: item.children.length > 4 ? '420px' : '280px' }}
                    onMouseEnter={() => handleMouseEnter(item.label)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className={item.children.length > 4 ? 'grid grid-cols-2 gap-0 p-2' : 'flex flex-col p-2'}>
                      {item.children.map((child) => (
                        <a
                          key={child.href}
                          href={child.href}
                          onClick={(e) => { e.preventDefault(); navigate(child.href); setActiveDropdown(null); }}
                          className={`flex items-start gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer group ${
                            location.pathname === child.href
                              ? 'bg-firmai-green/10 text-firmai-green'
                              : 'text-firmai-text hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <span className="w-8 h-8 flex items-center justify-center bg-firmai-card border border-firmai-border rounded-lg shrink-0 group-hover:border-firmai-green/30 transition-colors duration-200">
                            <i className={`${child.icon} text-sm ${location.pathname === child.href ? 'text-firmai-green' : 'text-firmai-muted group-hover:text-firmai-green'}`} />
                          </span>
                          <div>
                            <div className="text-sm font-medium leading-snug">{child.label}</div>
                            {'desc' in child && child.desc && (
                              <div className="text-xs text-firmai-muted mt-0.5 group-hover:text-firmai-text transition-colors">{child.desc}</div>
                            )}
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <a
              href="/login"
              onClick={(e) => { e.preventDefault(); navigate('/login'); }}
              className="px-4 py-2 text-sm font-medium text-firmai-text border border-firmai-border rounded-md hover:border-firmai-green/50 hover:text-firmai-green transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              客户登录
            </a>
            <a
              href="/contact"
              onClick={(e) => { e.preventDefault(); navigate('/contact'); }}
              className="px-4 py-2 text-sm font-medium text-firmai-dark bg-firmai-green rounded-md hover:bg-firmai-green/90 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              免费咨询
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="xl:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="xl:hidden bg-firmai-dark/98 backdrop-blur-md border-t border-firmai-border max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {navItems.map((item) => (
              <div key={item.label}>
                <div className="flex items-center justify-between">
                  <a
                    href={item.href}
                    onClick={(e) => {
                      if (!item.hasDropdown) {
                        handleNavClick(e, item);
                      } else {
                        e.preventDefault();
                        setMobileExpanded(mobileExpanded === item.label ? null : item.label);
                      }
                    }}
                    className={`flex-1 flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                      isActive(item) ? 'text-firmai-green bg-firmai-green/10' : 'text-firmai-text hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {item.label}
                  </a>
                  {item.hasDropdown && (
                    <button
                      onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                      className="w-9 h-9 flex items-center justify-center text-firmai-muted cursor-pointer"
                    >
                      <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileExpanded === item.label ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>
                {item.hasDropdown && item.children && mobileExpanded === item.label && (
                  <div className="pl-4 mt-1 space-y-0.5 border-l border-firmai-border ml-3">
                    {item.children.map((child) => (
                      <a
                        key={child.href}
                        href={child.href}
                        onClick={(e) => { e.preventDefault(); navigate(child.href); setMobileOpen(false); }}
                        className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg cursor-pointer ${
                          location.pathname === child.href
                            ? 'text-firmai-green'
                            : 'text-firmai-muted hover:text-firmai-green'
                        }`}
                      >
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className={`${child.icon} text-xs`} />
                        </span>
                        {child.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="flex flex-col gap-2 pt-3 border-t border-firmai-border">
              <a
                href="/login"
                onClick={(e) => { e.preventDefault(); navigate('/login'); setMobileOpen(false); }}
                className="block text-center px-4 py-2.5 text-sm font-medium text-firmai-text border border-firmai-border rounded-md cursor-pointer"
              >
                客户登录
              </a>
              <a
                href="/contact"
                onClick={(e) => { e.preventDefault(); navigate('/contact'); setMobileOpen(false); }}
                className="block text-center px-4 py-2.5 text-sm font-medium text-firmai-dark bg-firmai-green rounded-md cursor-pointer"
              >
                免费咨询
              </a>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes dropdown {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-dropdown { animation: dropdown 0.18s ease-out; }
      `}</style>
    </nav>
  );
}